import React, { createContext, useContext, useState, useEffect } from 'react';

const CookieContext = createContext();

export const useCookieConsent = () => {
  const context = useContext(CookieContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieProvider');
  }
  return context;
};

const COOKIE_CONSENT_KEY = 'pwgen-cookie-consent';
const COOKIE_PREFERENCES_KEY = 'pwgen-cookie-preferences';

const defaultPreferences = {
  necessary: true, // Always true, can't be disabled
  marketing: false,
  functional: false,
};

export const CookieProvider = ({ children }) => {
  const [consentGiven, setConsentGiven] = useState(null); // null = not decided, true = accepted, false = declined
  const [showBanner, setShowBanner] = useState(false);
  const [preferences, setPreferences] = useState(defaultPreferences);

  // Load consent and preferences from localStorage
  useEffect(() => {
    const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    const savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);

    if (savedConsent) {
      const consentData = JSON.parse(savedConsent);
      setConsentGiven(consentData.given);
      setShowBanner(false);
    } else {
      setShowBanner(true);
    }

    if (savedPreferences) {
      setPreferences(JSON.parse(savedPreferences));
    }
  }, []);

  // Save consent to localStorage
  const saveConsent = (given, prefs = preferences) => {
    const consentData = {
      given,
      timestamp: new Date().toISOString(),
      preferences: prefs,
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData));
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    setConsentGiven(given);
    setPreferences(prefs);
    setShowBanner(false);
  };

  // Accept all cookies
  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      marketing: true,
      functional: true,
    };
    saveConsent(true, allAccepted);
  };

  // Decline all non-necessary cookies
  const declineAll = () => {
    const onlyNecessary = {
      necessary: true,
      marketing: false,
      functional: false,
    };
    saveConsent(false, onlyNecessary);
  };

  // Save custom preferences
  const savePreferences = (newPreferences) => {
    const updatedPreferences = {
      ...newPreferences,
      necessary: true, // Always true
    };
    const hasAcceptedAny = Object.values(updatedPreferences).some(
      (value, index) => index > 0 && value
    );
    saveConsent(hasAcceptedAny, updatedPreferences);
  };

  // Reset consent (for testing or user request)
  const resetConsent = () => {
    localStorage.removeItem(COOKIE_CONSENT_KEY);
    localStorage.removeItem(COOKIE_PREFERENCES_KEY);
    setConsentGiven(null);
    setPreferences(defaultPreferences);
    setShowBanner(true);
  };

  // Check if specific cookie type is allowed
  const isAllowed = (cookieType) => {
    if (consentGiven === null) return cookieType === 'necessary';
    return preferences[cookieType] === true;
  };

  // Get consent status
  const getConsentStatus = () => {
    return {
      consentGiven,
      preferences,
      isAllowed,
      showBanner,
    };
  };

  const value = {
    consentGiven,
    showBanner,
    preferences,
    acceptAll,
    declineAll,
    savePreferences,
    resetConsent,
    isAllowed,
    getConsentStatus,
  };

  return (
    <CookieContext.Provider value={value}>
      {children}
    </CookieContext.Provider>
  );
};